// WhatsApp Setup — Phase 6 wires real Baileys pairing through Socket.io.
// QR and status updates land here live; the "I've scanned it" dev shortcut
// is gone — the real connection.update from WhatsApp drives the transition.

import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft,
  Plus,
  Power,
  PowerOff,
  QrCode,
  Trash2,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import type { WhatsAppSessionItem, WhatsAppStatus } from '@qualibot/shared';
import { Card } from '@/components/primitives/Card';
import { Badge } from '@/components/primitives/Badge';
import { StatusBadge } from '@/components/primitives/StatusBadge';
import { Btn } from '@/components/primitives/Btn';
import { Empty } from '@/components/primitives/Empty';
import { PageContainer, PageHeader, SectionLabel } from '@/components/PageHeader';
import {
  bindChannelAgent,
  connectWhatsAppSession,
  createWhatsAppSession,
  deleteWhatsAppSession,
  disconnectWhatsAppSession,
  listChannels,
  listWhatsAppSessions,
  unbindChannelAgent,
} from '@/lib/channels-api';
import { useSocketEvent } from '@/lib/socket';
import { listAgents } from '@/lib/agents-api';
import { extractApiError } from '@/lib/api-client';
import { humanizeEnum } from '@/lib/format';

const PHASE_NOTE =
  'Click Connect to start a real Baileys pairing. QR and status update live over Socket.io.';

export function WhatsAppPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const sessions = useQuery({ queryKey: ['wa-sessions'], queryFn: listWhatsAppSessions });
  const channels = useQuery({ queryKey: ['channels'], queryFn: listChannels });
  const agents = useQuery({ queryKey: ['agents'], queryFn: listAgents });

  const whatsappChannel = channels.data?.find((c) => c.type === 'WHATSAPP') ?? null;

  const [activeId, setActiveId] = useState<string | null>(null);
  useEffect(() => {
    // Auto-focus the most recent session, or the one we just created.
    if (!activeId && sessions.data?.length) setActiveId(sessions.data[0]!.id);
  }, [sessions.data, activeId]);

  const active = useMemo(
    () => sessions.data?.find((s) => s.id === activeId) ?? null,
    [sessions.data, activeId],
  );

  // Live socket updates — patch the session row inline so the UI flips
  // without a refetch round-trip.
  useSocketEvent<{ sessionId: string; qrCode: string }>('whatsapp:qr', (payload) => {
    queryClient.setQueryData<WhatsAppSessionItem[] | undefined>(['wa-sessions'], (prev) =>
      (prev ?? []).map((s) =>
        s.id === payload.sessionId ? { ...s, qrCode: payload.qrCode, status: 'QR_PENDING' } : s,
      ),
    );
  });
  useSocketEvent<{ sessionId: string; status: WhatsAppStatus }>('whatsapp:status', (payload) => {
    queryClient.setQueryData<WhatsAppSessionItem[] | undefined>(['wa-sessions'], (prev) =>
      (prev ?? []).map((s) =>
        s.id === payload.sessionId
          ? {
              ...s,
              status: payload.status,
              // Connected clears the QR; disconnected clears it too.
              qrCode: payload.status === 'QR_PENDING' ? s.qrCode : null,
              lastConnectedAt:
                payload.status === 'CONNECTED' ? new Date().toISOString() : s.lastConnectedAt,
            }
          : s,
      ),
    );
    // Channel binding status may also change downstream; nudge the cache.
    if (payload.status === 'CONNECTED' || payload.status === 'DISCONNECTED') {
      queryClient.invalidateQueries({ queryKey: ['channels'] });
    }
  });

  return (
    <PageContainer>
      <PageHeader
        title="WhatsApp"
        description={PHASE_NOTE}
        actions={
          <Btn icon={ArrowLeft} onClick={() => navigate('/channels')}>
            All channels
          </Btn>
        }
      />

      <div
        className="grid gap-3.5"
        style={{ gridTemplateColumns: '1.1fr 1fr' }}
      >
        <Card pad={0}>
          <div className="px-[18px] py-3 border-b border-border flex items-center justify-between">
            <span className="font-semibold text-[13.5px]">Connected numbers</span>
            <Badge>{sessions.data?.length ?? 0} session(s)</Badge>
          </div>
          {sessions.isLoading ? (
            <div className="text-[13px] text-text-3 py-8 text-center">Loading…</div>
          ) : !sessions.data?.length ? (
            <div className="p-6">
              <Empty
                title="No WhatsApp number yet"
                body="Add a number on the right to start the QR pairing flow."
              />
            </div>
          ) : (
            <div className="flex flex-col">
              {sessions.data.map((s) => (
                <button
                  key={s.id}
                  onClick={() => setActiveId(s.id)}
                  className="text-left px-[18px] py-3 border-b border-border last:border-b-0 hover:bg-surface-2 flex items-center justify-between"
                  style={{
                    background: s.id === activeId ? 'var(--surface-2)' : 'transparent',
                  }}
                >
                  <div>
                    <div className="font-medium text-[13.5px]" style={{ fontFamily: 'var(--font-mono)' }}>
                      {s.phoneNumber}
                    </div>
                    <div className="text-[11.5px] text-text-3 mt-0.5">
                      {s.sessionName} · {s.boundAgentName ?? 'no agent bound'}
                    </div>
                  </div>
                  <StatusBadge status={humanizeEnum(s.status)} />
                </button>
              ))}
            </div>
          )}
        </Card>

        <AddSessionCard
          onCreated={(s) => {
            setActiveId(s.id);
            queryClient.invalidateQueries({ queryKey: ['wa-sessions'] });
            queryClient.invalidateQueries({ queryKey: ['channels'] });
          }}
        />
      </div>

      {active && (
        <SessionDetail
          session={active}
          channelId={whatsappChannel?.id ?? null}
          agentOptions={agents.data ?? []}
          boundAgentId={
            whatsappChannel
              ? agents.data?.find((a) => whatsappChannel.boundAgentNames.includes(a.name))?.id ?? null
              : null
          }
          onRefresh={() => queryClient.invalidateQueries({ queryKey: ['wa-sessions'] })}
          onChannelChange={() => queryClient.invalidateQueries({ queryKey: ['channels'] })}
          onDeleted={() => {
            setActiveId(null);
            queryClient.invalidateQueries({ queryKey: ['wa-sessions'] });
          }}
        />
      )}
    </PageContainer>
  );
}

function AddSessionCard({ onCreated }: { onCreated: (s: WhatsAppSessionItem) => void }) {
  const [phone, setPhone] = useState('+971');
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: createWhatsAppSession,
    onSuccess: (s) => {
      onCreated(s);
      setPhone('+971');
      setName('');
    },
    onError: (err) => setError(extractApiError(err)),
  });

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    mutation.mutate({ phoneNumber: phone.trim(), displayName: name.trim() || undefined });
  }

  return (
    <Card>
      <div className="font-semibold text-[13.5px] mb-3">Add WhatsApp number</div>
      <form onSubmit={handleSubmit} className="flex flex-col gap-3.5">
        <div className="flex flex-col gap-1.5">
          <label className="text-[12px] font-semibold text-text-2">Phone number</label>
          <input
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            className="h-10 px-3 rounded-[10px] border border-border-strong bg-surface text-[13.5px] outline-none focus:border-accent"
            style={{ fontFamily: 'var(--font-mono)' }}
          />
          <span className="text-[11.5px] text-text-3">
            International format. The number must be reachable from the WhatsApp Web app on your phone.
          </span>
        </div>
        <div className="flex flex-col gap-1.5">
          <label className="text-[12px] font-semibold text-text-2">Display name (optional)</label>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={`WhatsApp · ${phone}`}
            className="h-10 px-3 rounded-[10px] border border-border-strong bg-surface text-[13.5px] outline-none focus:border-accent"
          />
        </div>
        {error && (
          <div
            role="alert"
            className="px-3 py-2 rounded-md text-[12.5px]"
            style={{ background: 'var(--err-soft)', color: 'var(--err)' }}
          >
            {error}
          </div>
        )}
        <Btn type="submit" kind="primary" icon={Plus} disabled={mutation.isPending}>
          {mutation.isPending ? 'Creating…' : 'Create session and show QR'}
        </Btn>
      </form>
    </Card>
  );
}

function SessionDetail({
  session,
  channelId,
  agentOptions,
  boundAgentId,
  onRefresh,
  onChannelChange,
  onDeleted,
}: {
  session: WhatsAppSessionItem;
  channelId: string | null;
  agentOptions: { id: string; name: string }[];
  boundAgentId: string | null;
  onRefresh: () => void;
  onChannelChange: () => void;
  onDeleted: () => void;
}) {
  const connect = useMutation({
    mutationFn: () => connectWhatsAppSession(session.id),
    onSuccess: onRefresh,
  });
  const disconnect = useMutation({
    mutationFn: () => disconnectWhatsAppSession(session.id),
    onSuccess: onRefresh,
  });
  const remove = useMutation({
    mutationFn: () => deleteWhatsAppSession(session.id),
    onSuccess: onDeleted,
  });

  const [confirmDelete, setConfirmDelete] = useState(false);

  // Bound agent dropdown — controlled, applies on change.
  const [selectedAgentId, setSelectedAgentId] = useState<string | ''>(boundAgentId ?? '');
  useEffect(() => {
    setSelectedAgentId(boundAgentId ?? '');
  }, [boundAgentId]);

  const bind = useMutation({
    mutationFn: async (agentId: string) => {
      if (!channelId) throw new Error('No WhatsApp channel');
      return bindChannelAgent(channelId, { agentId, enabled: true });
    },
    onSuccess: () => {
      onChannelChange();
      onRefresh();
    },
  });
  const unbind = useMutation({
    mutationFn: async () => {
      if (!channelId || !boundAgentId) return;
      return unbindChannelAgent(channelId, boundAgentId);
    },
    onSuccess: () => {
      onChannelChange();
      onRefresh();
    },
  });

  return (
    <div className="grid gap-3.5 mt-4" style={{ gridTemplateColumns: '1fr 1fr' }}>
      <Card>
        <div className="flex items-center justify-between mb-3">
          <div className="font-semibold text-[13.5px]">QR pairing</div>
          <StatusBadge status={humanizeEnum(session.status)} />
        </div>

        <div className="flex flex-col items-center justify-center py-3">
          {session.qrCode ? (
            <img
              src={session.qrCode}
              alt="WhatsApp pairing QR"
              className="w-[220px] h-[220px] rounded-md"
              style={{ background: '#fff', border: '1px solid var(--border)' }}
            />
          ) : (
            <div
              className="w-[220px] h-[220px] rounded-md flex items-center justify-center text-text-3"
              style={{ background: 'var(--bg-sunken)', border: '1px dashed var(--border-strong)' }}
            >
              <div className="text-center">
                <QrCode size={28} strokeWidth={1.4} className="mx-auto mb-2 opacity-50" />
                <div className="text-[12.5px]">No QR yet</div>
                <div className="text-[11.5px] mt-0.5">Click "Connect" to generate.</div>
              </div>
            </div>
          )}
          <div className="text-[11.5px] text-text-3 mt-2 max-w-[260px] text-center">
            Open WhatsApp → Settings → Linked Devices → Link a Device, then scan this code.
            {session.status === 'QR_PENDING' && (
              <>
                <br />
                <span>QR refreshes automatically every ~20s.</span>
              </>
            )}
          </div>
        </div>

        <SectionLabel>Session actions</SectionLabel>
        <div className="flex flex-wrap items-center gap-2">
          {session.status !== 'CONNECTED' && (
            <Btn
              kind={session.status === 'NOT_STARTED' ? 'primary' : 'default'}
              icon={Power}
              onClick={() => connect.mutate()}
              disabled={connect.isPending}
            >
              {connect.isPending
                ? 'Starting…'
                : session.status === 'QR_PENDING'
                  ? 'Refresh QR'
                  : 'Connect'}
            </Btn>
          )}
          {session.status === 'CONNECTED' && (
            <Btn icon={PowerOff} onClick={() => disconnect.mutate()} disabled={disconnect.isPending}>
              {disconnect.isPending ? 'Disconnecting…' : 'Disconnect'}
            </Btn>
          )}
          {!confirmDelete ? (
            <Btn kind="danger" icon={Trash2} onClick={() => setConfirmDelete(true)}>
              Delete
            </Btn>
          ) : (
            <>
              <Btn onClick={() => setConfirmDelete(false)}>Cancel</Btn>
              <Btn
                kind="danger"
                icon={Trash2}
                onClick={() => remove.mutate()}
                disabled={remove.isPending}
              >
                {remove.isPending ? 'Deleting…' : 'Confirm delete'}
              </Btn>
            </>
          )}
        </div>
      </Card>

      <Card>
        <div className="font-semibold text-[13.5px] mb-3">Session details</div>
        <Detail label="Phone" value={session.phoneNumber} mono />
        <Detail label="Name" value={session.sessionName} />
        <Detail label="Status" value={humanizeEnum(session.status)} />
        <Detail
          label="Last connected"
          value={session.lastConnectedAt ? new Date(session.lastConnectedAt).toLocaleString() : '—'}
        />
        <Detail
          label="Last disconnected"
          value={
            session.lastDisconnectedAt
              ? new Date(session.lastDisconnectedAt).toLocaleString()
              : '—'
          }
        />

        <SectionLabel>Bound agent</SectionLabel>
        <div className="flex flex-col gap-1.5">
          <select
            value={selectedAgentId}
            onChange={(e) => {
              const v = e.target.value;
              setSelectedAgentId(v);
              if (v) bind.mutate(v);
            }}
            disabled={!channelId || bind.isPending}
            className="h-10 px-3 rounded-[10px] border border-border-strong bg-surface text-[13.5px]"
          >
            <option value="">Choose an agent…</option>
            {agentOptions.map((a) => (
              <option key={a.id} value={a.id}>
                {a.name}
              </option>
            ))}
          </select>
          {boundAgentId && (
            <div className="flex items-center justify-between text-[12.5px] text-text-3 mt-1">
              <span>Bound to {agentOptions.find((a) => a.id === boundAgentId)?.name}</span>
              <Btn size="sm" onClick={() => unbind.mutate()} disabled={unbind.isPending}>
                Unbind
              </Btn>
            </div>
          )}
          {!channelId && (
            <div className="text-[11.5px] text-warn mt-1" style={{ color: 'var(--warn)' }}>
              No WhatsApp channel yet — create a session first.
            </div>
          )}
        </div>
      </Card>
    </div>
  );
}

function Detail({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="grid items-center py-1.5 text-[13px]" style={{ gridTemplateColumns: '140px 1fr' }}>
      <span className="text-text-3">{label}</span>
      <span
        style={{
          fontFamily: mono ? 'var(--font-mono)' : 'var(--font-ui)',
          fontSize: mono ? 12 : 13,
        }}
      >
        {value}
      </span>
    </div>
  );
}
